import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Phone,
  Loader2,
  UserPlus,
  Briefcase,
  User,
  AtSign,
  Tag,
  Plus,
  ChevronDown,
  Send,
  Smile,
  StickyNote,
} from "lucide-react";
import {
  clientsApi,
  contactsApi,
  conversationsApi,
  inspectorApi,
  internalCommentsApi,
  usersApi,
} from "../../services/api";
import { formatPhoneNumber } from "../../utils/formatPhone";

// ── Types ─────────────────────────────────────────────────────────────

interface Participant {
  kind: string;
  userId?: string;
  contactId?: string;
  displayName?: string;
  email?: string;
  phoneE164?: string;
  role?: string;
}

interface ConversationClientInspectorDrawerProps {
  open: boolean;
  conversation: any;
  onClose: () => void;
}

// ── Preset label options ──────────────────────────────────────────────

const ROLE_OPTIONS = [
  { value: "client", label: "Client" },
  { value: "dependent", label: "Dependent" },
  { value: "spouse", label: "Spouse" },
  { value: "external_counsel", label: "External Counsel" },
  { value: "banking_partner", label: "Banking Partner" },
  { value: "opposing_counsel", label: "Opposing Counsel" },
  { value: "realtor", label: "Realtor" },
  { value: "lender", label: "Lender" },
  { value: "witness", label: "Witness" },
  { value: "expert", label: "Expert" },
  { value: "mediator", label: "Mediator" },
  { value: "government", label: "Government" },
  { value: "vendor", label: "Vendor" },
  { value: "referral_source", label: "Referral Source" },
  { value: "other", label: "Other" },
];

const LABEL_PRESETS = [
  "Client",
  "Dependent",
  "Spouse",
  "External Counsel",
  "Banking Partner",
  "Opposing Counsel",
  "Realtor",
  "Lender",
  "Vendor",
  "Referral Source",
  "Expert Witness",
  "Co-Counsel",
  "Guarantor",
  "Power of Attorney",
  "Executor",
  "Beneficiary",
  "Trustee",
];

// ── Helpers ───────────────────────────────────────────────────────────

const isPhoneNumber = (name: string) => /^\+?\d[\d\s()-]*$/.test(name.trim());

const getInitials = (name: string) => {
  if (isPhoneNumber(name)) return null;
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
};

const formatPhone = (phone?: string) => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

// Avatar colors — consistent per name
const AVATAR_COLORS = [
  "#F59E0B", "#EF4444", "#8B5CF6", "#3B82F6", "#10B981",
  "#EC4899", "#F97316", "#06B6D4", "#6366F1", "#14B8A6",
];

const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// ── Internal Comment Types ────────────────────────────────────────────

interface InternalNote {
  _id: string;
  body: string;
  authorId: { _id: string; name?: string; email?: string } | string;
  authorName: string | null;
  createdAt: string;
}

// ── Component ─────────────────────────────────────────────────────────

const ConversationClientInspectorDrawer: React.FC<ConversationClientInspectorDrawerProps> = ({
  open,
  conversation,
  onClose,
}) => {
  // ── State ─────────────────────────────────────────────────────────
  const [contact, setContact] = useState<any>(null);
  const [client, setClient] = useState<any>(null);
  const [savedContactId, setSavedContactId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingAsClient, setAddingAsClient] = useState(false);

  // Inline editing
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);

  // Role dropdown
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);

  // Labels
  const [showLabelPicker, setShowLabelPicker] = useState(false);
  const [labelSearch, setLabelSearch] = useState("");

  // Tags
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagValue, setTagValue] = useState("");

  // Contact notes editing
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesEditValue, setNotesEditValue] = useState("");
  const notesEditRef = useRef<HTMLTextAreaElement>(null);

  // Save-as-contact
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveFirstName, setSaveFirstName] = useState("");
  const [saveLastName, setSaveLastName] = useState("");
  const [saving, setSaving] = useState(false);

  // Notes
  const [notes, setNotes] = useState<InternalNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [noteSending, setNoteSending] = useState(false);
  const noteInputRef = useRef<HTMLTextAreaElement>(null);

  // @mention state
  const [teamMembers, setTeamMembers] = useState<Array<{ _id: string; name: string }>>([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionStartPos, setMentionStartPos] = useState<number | null>(null);

  // Responsive
  const [isMobile, setIsMobile] = useState(false);

  // Refs
  const labelPickerRef = useRef<HTMLDivElement>(null);
  const roleDropdownRef = useRef<HTMLDivElement>(null);

  // Extract participant info
  const primary = conversation?.participants?.find((p: Participant) => p.role === "primary");
  const contactId = savedContactId || primary?.contactId;
  const displayName =
    contact?.displayName ||
    (contact?.person?.firstName ? `${contact.person.firstName} ${contact.person.lastName || ""}`.trim() : null) ||
    primary?.displayName ||
    "";
  const contactPhone = contact?.phones?.[0]?.number || primary?.phoneE164 || "";
  const contactEmail = contact?.emails?.[0]?.email || primary?.email || "";
  const contactCompany = contact?.company || "";
  const contactRole = contact?.role || "";
  const contactLabels: string[] = contact?.labels || [];
  const contactTags: string[] = contact?.tags || [];
  const contactNotes: string = contact?.notes || "";

  // ── Data fetching ────────────────────────────────────────────────

  const fetchInspectorData = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await inspectorApi.get(contactId);
      const data = res.data;
      setContact(data.contact || null);
      setClient(data.client || null);
    } catch {
      setError("Failed to load contact details");
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  const fetchNotes = useCallback(async () => {
    if (!conversation?._id) return;
    setNotesLoading(true);
    try {
      // Fetch notes for the conversation (not a specific entity)
      const res = await internalCommentsApi.list({
        conversationId: conversation._id,
        entityType: "message",
        entityId: conversation._id,
      });
      const fetched = res.data?.data || res.data || [];
      setNotes(fetched);
    } catch {
      /* ignore */
    } finally {
      setNotesLoading(false);
    }
  }, [conversation?._id]);

  useEffect(() => {
    if (!open || !conversation) return;
    if (contactId) {
      fetchInspectorData();
    } else {
      setContact(null);
      setClient(null);
      setSavedContactId(null);
      setLoading(false);
      setError(null);
    }
    fetchNotes();
  }, [open, conversation?._id, contactId, fetchInspectorData, fetchNotes]);

  // Fetch team members for @mention
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await usersApi.list();
        const users = res.data?.data || res.data || [];
        setTeamMembers(
          users.map((u: any) => ({ _id: u._id, name: u.name || u.email || "Unknown" }))
        );
      } catch { /* ignore */ }
    };
    fetchTeam();
  }, []);

  // ── ESC key + outside click ────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (editingField) {
          setEditingField(null);
          return;
        }
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose, editingField]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (labelPickerRef.current && !labelPickerRef.current.contains(e.target as Node)) {
        setShowLabelPicker(false);
      }
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(e.target as Node)) {
        setShowRoleDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // ── Responsive breakpoint ────────────────────────────────────────

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 900);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ── Inline edit handlers ─────────────────────────────────────────

  const saveField = async (field: string, value: any) => {
    if (!contactId) return;
    try {
      await inspectorApi.update(contactId, { [field]: value });
    } catch { /* ignore */ }
  };

  const startEditing = (field: string, currentValue: string) => {
    if (!contactId) return;
    setEditingField(field);
    setEditValue(currentValue);
    setTimeout(() => editInputRef.current?.focus(), 0);
  };

  const commitEdit = (field: string) => {
    setEditingField(null);
    const val = editValue.trim();
    saveField(field, val || null);
    setContact((prev: any) => prev ? { ...prev, [field]: val || null } : prev);
  };

  const handleRoleSelect = (value: string, label: string) => {
    setShowRoleDropdown(false);
    saveField("role", label);
    setContact((prev: any) => prev ? { ...prev, role: label } : prev);
  };

  // ── Label handlers ────────────────────────────────────────────────

  const addLabel = (label: string) => {
    if (!contactId) return;
    const trimmed = label.trim();
    if (!trimmed || contactLabels.includes(trimmed)) return;
    const updated = [...contactLabels, trimmed];
    saveField("labels", updated);
    setContact((prev: any) => prev ? { ...prev, labels: updated } : prev);
    setLabelSearch("");
  };

  const removeLabel = (label: string) => {
    if (!contactId) return;
    const updated = contactLabels.filter((l) => l !== label);
    saveField("labels", updated);
    setContact((prev: any) => prev ? { ...prev, labels: updated } : prev);
  };

  // ── Tag handlers ──────────────────────────────────────────────────

  const addTag = (tag: string) => {
    if (!contactId) return;
    const trimmed = tag.trim();
    if (!trimmed || contactTags.includes(trimmed)) return;
    const updated = [...contactTags, trimmed];
    saveField("tags", updated);
    setContact((prev: any) => prev ? { ...prev, tags: updated } : prev);
    setTagValue("");
  };

  const removeTag = (tag: string) => {
    if (!contactId) return;
    const updated = contactTags.filter((t) => t !== tag);
    saveField("tags", updated);
    setContact((prev: any) => prev ? { ...prev, tags: updated } : prev);
  };

  // ── Save as contact ──────────────────────────────────────────────

  const handleSaveAsContact = async () => {
    if (!saveFirstName.trim()) return;
    setSaving(true);
    try {
      const fullName = `${saveFirstName.trim()} ${saveLastName.trim()}`.trim();
      const data: Record<string, any> = {
        displayName: fullName,
        category: "person",
        person: {
          firstName: saveFirstName.trim(),
          lastName: saveLastName.trim() || null,
        },
      };
      if (primary?.phoneE164) {
        data.phones = [{ label: "main", number: primary.phoneE164 }];
      }
      if (primary?.email) {
        data.emails = [{ label: "main", email: primary.email }];
      }
      const res = await contactsApi.create(data);
      const newContact = res.data;

      if (conversation?._id && newContact?._id) {
        const updatedParticipants = conversation.participants.map((p: Participant) =>
          p.role === "primary"
            ? { ...p, contactId: newContact._id, displayName: `${saveFirstName.trim()} ${saveLastName.trim()}`.trim() }
            : p
        );
        await conversationsApi.update(conversation._id, { participants: updatedParticipants });
      }

      setContact(newContact);
      setSavedContactId(newContact._id);
      setShowSaveForm(false);
      setSaveFirstName("");
      setSaveLastName("");
    } catch { /* ignore */ } finally {
      setSaving(false);
    }
  };

  // ── Notes handlers ────────────────────────────────────────────────

  const handleSendNote = async () => {
    if (!noteBody.trim() || noteSending || !conversation?._id) return;
    setNoteSending(true);
    try {
      await internalCommentsApi.create({
        conversationId: conversation._id,
        entityType: "message",
        entityId: conversation._id,
        body: noteBody.trim(),
      });
      setNoteBody("");
      fetchNotes();
    } catch { /* ignore */ } finally {
      setNoteSending(false);
    }
  };

  // @mention handling
  const filteredMembers = teamMembers.filter((m) =>
    m.name.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  const handleNoteInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setNoteBody(val);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf("@");

    if (atIndex !== -1) {
      const textBetween = textBeforeCursor.slice(atIndex + 1);
      const charBefore = atIndex > 0 ? val[atIndex - 1] : " ";
      if ((charBefore === " " || charBefore === "\n" || atIndex === 0) && !textBetween.includes("\n")) {
        setMentionStartPos(atIndex);
        setMentionSearch(textBetween);
        setShowMentionDropdown(true);
        setMentionIndex(0);
        return;
      }
    }
    setShowMentionDropdown(false);
    setMentionStartPos(null);
  };

  const insertMention = (memberName: string) => {
    if (mentionStartPos === null) return;
    const before = noteBody.slice(0, mentionStartPos);
    const ta = noteInputRef.current;
    const cursorPos = ta?.selectionStart ?? noteBody.length;
    const after = noteBody.slice(cursorPos);
    const newBody = `${before}@${memberName} ${after}`;
    setNoteBody(newBody);
    setShowMentionDropdown(false);
    setMentionStartPos(null);
    setTimeout(() => {
      if (ta) {
        const newPos = before.length + memberName.length + 2;
        ta.focus();
        ta.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleNoteKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentionDropdown && filteredMembers.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex((p) => (p + 1) % filteredMembers.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex((p) => (p - 1 + filteredMembers.length) % filteredMembers.length); return; }
      if (e.key === "Enter") { e.preventDefault(); insertMention(filteredMembers[mentionIndex].name); return; }
      if (e.key === "Escape") { e.preventDefault(); setShowMentionDropdown(false); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendNote();
    }
  };

  // Auto-resize notes textarea
  useEffect(() => {
    const ta = noteInputRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
  }, [noteBody]);

  // ── Phone / Email save handlers ────────────────────────────────────

  const savePhone = (value: string) => {
    if (!contactId) return;
    const trimmed = value.trim();
    const phones = trimmed ? [{ label: "main", number: trimmed }] : [];
    saveField("phones", phones);
    setContact((prev: any) => prev ? { ...prev, phones } : prev);
  };

  const saveEmail = (value: string) => {
    if (!contactId) return;
    const trimmed = value.trim();
    const emails = trimmed ? [{ label: "main", email: trimmed }] : [];
    saveField("emails", emails);
    setContact((prev: any) => prev ? { ...prev, emails } : prev);
  };

  // ── Add as Client ──────────────────────────────────────────────────

  const handleAddAsClient = async () => {
    if (!contactId || !contact) return;
    setAddingAsClient(true);
    try {
      const data: Record<string, any> = {
        displayName: contact.displayName || displayName,
        primaryContactId: contactId,
        type: "individual",
      };
      if (contact.person?.firstName) {
        data.individuals = {
          firstName: contact.person.firstName,
          lastName: contact.person.lastName || "",
        };
      }
      if (contact.emails?.length > 0) data.emails = contact.emails;
      if (contact.phones?.length > 0) data.phones = contact.phones;
      const res = await clientsApi.create(data);
      setClient(res.data);
    } catch { /* ignore */ } finally {
      setAddingAsClient(false);
    }
  };

  // ── Filtered label presets ────────────────────────────────────────

  const filteredPresets = LABEL_PRESETS.filter(
    (l) =>
      !contactLabels.includes(l) &&
      l.toLowerCase().includes(labelSearch.toLowerCase())
  );

  if (!open || !conversation) return null;

  // ── Render ───────────────────────────────────────────────────────

  // Editable field row
  const renderField = (
    icon: React.ReactNode,
    label: string,
    fieldKey: string,
    value: string,
    placeholder: string
  ) => (
    <div className="flex items-center gap-4 py-3">
      <div className="w-5 flex-shrink-0 flex justify-center text-slate-400">
        {icon}
      </div>
      <span className="text-sm text-slate-500 w-16 flex-shrink-0">{label}</span>
      <div className="flex-1 min-w-0">
        {editingField === fieldKey ? (
          <input
            ref={editInputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => commitEdit(fieldKey)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEdit(fieldKey);
              if (e.key === "Escape") setEditingField(null);
            }}
            className="w-full text-sm text-slate-800 bg-transparent border-b border-slate-300 focus:border-slate-500 outline-none py-0.5"
          />
        ) : (
          <span
            className={`text-sm cursor-pointer transition-colors ${
              value
                ? "text-slate-800 hover:text-slate-600"
                : "text-slate-400 hover:text-slate-500"
            }`}
            onClick={() => startEditing(fieldKey, value)}
          >
            {value || placeholder}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className={`h-full border-l border-slate-200 bg-white overflow-hidden flex-shrink-0 ${
        isMobile ? "fixed inset-0 z-50 border-l-0" : "relative"
      }`}
      style={{ width: isMobile ? "100%" : 400 }}
    >
      <div className="h-full flex flex-col" style={{ width: isMobile ? "100%" : 400 }}>
        {/* Close button */}
        <div className="flex items-center justify-end px-4 pt-3">
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="flex flex-col items-center justify-center py-20 px-6">
              <p className="text-sm text-slate-500 mb-3">{error}</p>
              <button
                onClick={fetchInspectorData}
                className="text-sm font-medium text-slate-600 hover:text-slate-800 underline"
              >
                Try again
              </button>
            </div>
          )}

          {/* ── UNIFIED LAYOUT (works for both saved and unsaved contacts) ── */}
          {!loading && !error && (
            <>
              {/* ── CONTACT HEADER ── */}
              <div className="px-6 pt-2 pb-6">
                {/* Avatar */}
                <div className="flex flex-col items-center">
                  <div
                    className="h-20 w-20 rounded-full flex items-center justify-center mb-4"
                    style={{ backgroundColor: displayName && getInitials(displayName) ? getAvatarColor(displayName) : "#94a3b8" }}
                  >
                    {displayName && getInitials(displayName) ? (
                      <span className="text-white text-2xl font-bold leading-none">
                        {getInitials(displayName)}
                      </span>
                    ) : (
                      <User className="h-8 w-8 text-white" />
                    )}
                  </div>

                  {/* Name — inline save-as-contact for unknown, click-to-edit for known */}
                  {!contactId ? (
                    <>
                      {showSaveForm ? (
                        <div className="w-full max-w-[280px] space-y-2">
                          <input
                            type="text"
                            placeholder="First Name *"
                            value={saveFirstName}
                            onChange={(e) => setSaveFirstName(e.target.value)}
                            autoFocus
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-center focus:outline-none focus:border-slate-400"
                          />
                          <input
                            type="text"
                            placeholder="Last Name"
                            value={saveLastName}
                            onChange={(e) => setSaveLastName(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-center focus:outline-none focus:border-slate-400"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={handleSaveAsContact}
                              disabled={saving || !saveFirstName.trim()}
                              className="flex-1 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-50 hover:bg-slate-800 transition-colors"
                            >
                              {saving ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Save"}
                            </button>
                            <button
                              onClick={() => { setShowSaveForm(false); setSaveFirstName(""); setSaveLastName(""); }}
                              className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <h2
                          className="text-lg text-slate-400 text-center cursor-pointer hover:text-slate-500 transition-colors"
                          onClick={() => setShowSaveForm(true)}
                        >
                          Add a name{"\u2026"}
                        </h2>
                      )}
                    </>
                  ) : editingField === "displayName" ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => commitEdit("displayName")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitEdit("displayName");
                        if (e.key === "Escape") setEditingField(null);
                      }}
                      className="text-xl font-semibold text-slate-800 text-center border-b-2 border-slate-400 outline-none bg-transparent w-full max-w-[280px]"
                    />
                  ) : (
                    <h2
                      className={`text-xl font-semibold text-center cursor-pointer transition-colors ${
                        displayName ? "text-slate-800 hover:text-slate-600" : "text-slate-400 hover:text-slate-500"
                      }`}
                      onClick={() => startEditing("displayName", displayName)}
                    >
                      {displayName || "Add a name\u2026"}
                    </h2>
                  )}

                  {/* Labels (styled pills below name) */}
                  {contactLabels.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2 justify-center">
                      {contactLabels.map((label) => (
                        <span
                          key={label}
                          className="group inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors cursor-default"
                        >
                          {label}
                          <button
                            onClick={() => removeLabel(label)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-slate-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                </div>
              </div>

              {/* ── DETAIL FIELDS ── */}
              <div className="px-6 border-t border-slate-100">
                {/* Company */}
                <div className="flex items-center gap-4 py-3">
                  <div className="w-5 flex-shrink-0 flex justify-center text-slate-400">
                    <Briefcase className="h-4 w-4" />
                  </div>
                  <span className="text-sm text-slate-500 w-16 flex-shrink-0">Company</span>
                  <div className="flex-1 min-w-0">
                    {contactId && editingField === "company" ? (
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => commitEdit("company")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitEdit("company");
                          if (e.key === "Escape") setEditingField(null);
                        }}
                        className="w-full text-sm text-slate-800 bg-transparent border-b border-slate-300 focus:border-slate-500 outline-none py-0.5"
                      />
                    ) : (
                      <span
                        className={`text-sm transition-colors ${
                          contactCompany ? "text-slate-800" : "text-slate-400"
                        } ${contactId ? "cursor-pointer hover:text-slate-500" : ""}`}
                        onClick={() => contactId && startEditing("company", contactCompany)}
                      >
                        {contactCompany || "Set a company"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Role — dropdown select */}
                <div className="flex items-center gap-4 py-3 border-t border-slate-50">
                  <div className="w-5 flex-shrink-0 flex justify-center text-slate-400">
                    <User className="h-4 w-4" />
                  </div>
                  <span className="text-sm text-slate-500 w-16 flex-shrink-0">Role</span>
                  <div className="flex-1 min-w-0 relative" ref={roleDropdownRef}>
                    <button
                      onClick={() => contactId && setShowRoleDropdown(!showRoleDropdown)}
                      className={`text-sm transition-colors flex items-center gap-1 ${
                        contactRole ? "text-slate-800 hover:text-slate-600" : "text-slate-400 hover:text-slate-500"
                      } ${!contactId ? "cursor-default" : ""}`}
                    >
                      {contactRole || "Set a role"}
                      {contactId && <ChevronDown className="h-3 w-3 text-slate-400" />}
                    </button>

                    {showRoleDropdown && (
                      <div className="absolute top-full mt-1 left-0 w-52 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-50 max-h-56 overflow-y-auto">
                        {ROLE_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => handleRoleSelect(opt.value, opt.label)}
                            className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                              contactRole === opt.label
                                ? "bg-slate-100 text-slate-800 font-medium"
                                : "text-slate-700 hover:bg-slate-50"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-center gap-4 py-3 border-t border-slate-50">
                  <div className="w-5 flex-shrink-0 flex justify-center text-slate-400">
                    <Phone className="h-4 w-4" />
                  </div>
                  <span className="text-sm text-slate-500 w-16 flex-shrink-0">Phone</span>
                  <div className="flex-1 min-w-0">
                    {contactId && editingField === "phone" ? (
                      <input
                        ref={editInputRef}
                        type="tel"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => { setEditingField(null); savePhone(editValue); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { setEditingField(null); savePhone(editValue); }
                          if (e.key === "Escape") setEditingField(null);
                        }}
                        className="w-full text-sm text-slate-800 bg-transparent border-b border-slate-300 focus:border-slate-500 outline-none py-0.5"
                      />
                    ) : (
                      <span
                        className={`text-sm transition-colors ${
                          contactPhone ? "text-slate-800" : "text-slate-400"
                        } ${contactId ? "cursor-pointer hover:text-slate-500" : ""}`}
                        onClick={() => {
                          if (!contactId) return;
                          setEditingField("phone");
                          setEditValue(contactPhone);
                          setTimeout(() => editInputRef.current?.focus(), 0);
                        }}
                      >
                        {contactPhone ? formatPhone(contactPhone) : "Set a phone\u2026"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-center gap-4 py-3 border-t border-slate-50">
                  <div className="w-5 flex-shrink-0 flex justify-center text-slate-400">
                    <AtSign className="h-4 w-4" />
                  </div>
                  <span className="text-sm text-slate-500 w-16 flex-shrink-0">Email</span>
                  <div className="flex-1 min-w-0">
                    {contactId && editingField === "email" ? (
                      <input
                        ref={editInputRef}
                        type="email"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => { setEditingField(null); saveEmail(editValue); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { setEditingField(null); saveEmail(editValue); }
                          if (e.key === "Escape") setEditingField(null);
                        }}
                        className="w-full text-sm text-slate-800 bg-transparent border-b border-slate-300 focus:border-slate-500 outline-none py-0.5"
                      />
                    ) : (
                      <span
                        className={`text-sm transition-colors ${
                          contactEmail ? "text-slate-800" : "text-slate-400"
                        } ${contactId ? "cursor-pointer hover:text-slate-500" : ""}`}
                        onClick={() => {
                          if (!contactId) return;
                          setEditingField("email");
                          setEditValue(contactEmail);
                          setTimeout(() => editInputRef.current?.focus(), 0);
                        }}
                      >
                        {contactEmail || "Set an email\u2026"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Tags */}
                <div className="flex items-start gap-4 py-3 border-t border-slate-50">
                  <div className="w-5 flex-shrink-0 flex justify-center text-slate-400 mt-0.5">
                    <Tag className="h-4 w-4" />
                  </div>
                  <span className="text-sm text-slate-500 w-16 flex-shrink-0 mt-0.5">Tags</span>
                  <div className="flex-1 min-w-0">
                    {contactTags.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5 mb-1.5">
                        {contactTags.map((tag) => (
                          <span
                            key={tag}
                            className="group inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-600"
                          >
                            {tag}
                            <button
                              onClick={() => removeTag(tag)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-slate-600"
                            >
                              <X className="h-2.5 w-2.5" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {contactId ? (
                      showTagInput ? (
                        <input
                          type="text"
                          value={tagValue}
                          onChange={(e) => setTagValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && tagValue.trim()) { addTag(tagValue); }
                            if (e.key === "Escape") { setShowTagInput(false); setTagValue(""); }
                          }}
                          onBlur={() => {
                            if (tagValue.trim()) addTag(tagValue);
                            setShowTagInput(false);
                            setTagValue("");
                          }}
                          autoFocus
                          placeholder="Type a tag..."
                          className="text-sm text-slate-800 bg-transparent border-b border-slate-300 focus:border-slate-500 outline-none py-0.5 w-full"
                        />
                      ) : (
                        <button
                          onClick={() => setShowTagInput(true)}
                          className="text-sm text-slate-400 hover:text-slate-500 transition-colors"
                        >
                          {contactTags.length === 0 ? "Set tags\u2026" : "+ Add tag"}
                        </button>
                      )
                    ) : (
                      <span className="text-sm text-slate-400">Set tags{"\u2026"}</span>
                    )}
                  </div>
                </div>

                {/* Contact Notes */}
                <div className="flex items-start gap-4 py-3 border-t border-slate-50">
                  <div className="w-5 flex-shrink-0 flex justify-center text-slate-400 mt-0.5">
                    <StickyNote className="h-4 w-4" />
                  </div>
                  <span className="text-sm text-slate-500 w-16 flex-shrink-0 mt-0.5">Notes</span>
                  <div className="flex-1 min-w-0">
                    {contactId && editingNotes ? (
                      <textarea
                        ref={notesEditRef}
                        value={notesEditValue}
                        onChange={(e) => setNotesEditValue(e.target.value)}
                        onBlur={() => {
                          setEditingNotes(false);
                          saveField("notes", notesEditValue.trim() || null);
                          setContact((prev: any) => prev ? { ...prev, notes: notesEditValue.trim() || null } : prev);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") setEditingNotes(false);
                        }}
                        autoFocus
                        rows={3}
                        className="w-full text-sm text-slate-800 bg-transparent border border-slate-300 rounded-lg focus:border-slate-500 outline-none p-2 resize-none"
                      />
                    ) : (
                      <span
                        className={`text-sm transition-colors whitespace-pre-wrap ${
                          contactNotes ? "text-slate-800" : "text-slate-400"
                        } ${contactId ? "cursor-pointer hover:text-slate-500" : ""}`}
                        onClick={() => {
                          if (!contactId) return;
                          setEditingNotes(true);
                          setNotesEditValue(contactNotes);
                          setTimeout(() => notesEditRef.current?.focus(), 0);
                        }}
                      >
                        {contactNotes || "Add notes\u2026"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Add as Client */}
                {contactId && !client && (
                  <div className="py-3 border-t border-slate-50">
                    <button
                      onClick={handleAddAsClient}
                      disabled={addingAsClient}
                      className="flex items-center gap-2 text-sm font-medium transition-colors"
                      style={{ color: "var(--color-primary)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-primary-hover)")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-primary)")}
                    >
                      {addingAsClient ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <UserPlus className="h-4 w-4" />
                      )}
                      Add as Client
                    </button>
                  </div>
                )}

                {client && (
                  <div className="py-3 border-t border-slate-50">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-xs font-medium text-emerald-700">
                      <User className="h-3 w-3" />
                      Client
                    </span>
                  </div>
                )}

                {/* Add a label */}
                {contactId && (
                  <div className="py-3 border-t border-slate-50 relative" ref={labelPickerRef}>
                    <button
                      onClick={() => setShowLabelPicker(!showLabelPicker)}
                      className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-500 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Add a label
                    </button>

                    {showLabelPicker && (
                      <div className="absolute bottom-full mb-1 left-0 w-60 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                        <div className="px-3 mb-2">
                          <input
                            type="text"
                            value={labelSearch}
                            onChange={(e) => setLabelSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && labelSearch.trim()) {
                                addLabel(labelSearch);
                                setShowLabelPicker(false);
                              }
                            }}
                            placeholder="Search or create label..."
                            autoFocus
                            className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-slate-400"
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {filteredPresets.map((preset) => (
                            <button
                              key={preset}
                              onClick={() => {
                                addLabel(preset);
                                setShowLabelPicker(false);
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                            >
                              {preset}
                            </button>
                          ))}
                          {labelSearch.trim() && !LABEL_PRESETS.includes(labelSearch.trim()) && (
                            <button
                              onClick={() => {
                                addLabel(labelSearch);
                                setShowLabelPicker(false);
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-50 transition-colors"
                            >
                              Create &ldquo;{labelSearch.trim()}&rdquo;
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── NOTES SECTION ── */}
              <div className="px-6 pt-4 pb-6 border-t border-slate-100">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-slate-700">Notes</span>
                  <span className="text-sm text-slate-400">{notes.length}</span>
                </div>

                {/* Notes composer */}
                <div className="relative">
                  <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                    <textarea
                      ref={noteInputRef}
                      value={noteBody}
                      onChange={handleNoteInputChange}
                      onKeyDown={handleNoteKeyDown}
                      placeholder="Write a note..."
                      rows={3}
                      className="w-full px-3 pt-3 pb-8 text-sm bg-transparent outline-none resize-none placeholder-slate-400"
                    />
                    <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            const ta = noteInputRef.current;
                            if (!ta) return;
                            const pos = ta.selectionStart;
                            const before = noteBody.slice(0, pos);
                            const after = noteBody.slice(pos);
                            setNoteBody(`${before}@${after}`);
                            setTimeout(() => {
                              ta.focus();
                              ta.setSelectionRange(pos + 1, pos + 1);
                            }, 0);
                          }}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors"
                          title="Mention someone"
                        >
                          <AtSign className="h-3.5 w-3.5" />
                        </button>
                        <button
                          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors"
                          title="Emoji"
                        >
                          <Smile className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={handleSendNote}
                        disabled={!noteBody.trim() || noteSending}
                        className={`h-7 w-7 rounded-full flex items-center justify-center transition-colors ${
                          noteBody.trim()
                            ? "bg-slate-800 text-white hover:bg-slate-700"
                            : "bg-slate-200 text-slate-400"
                        }`}
                      >
                        {noteSending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Send className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* @mention dropdown */}
                  {showMentionDropdown && filteredMembers.length > 0 && (
                    <div className="absolute bottom-full mb-1 left-0 w-52 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-50 max-h-40 overflow-y-auto">
                      {filteredMembers.map((member, idx) => (
                        <button
                          key={member._id}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => insertMention(member.name)}
                          className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                            idx === mentionIndex ? "bg-slate-100 text-slate-800" : "text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          {member.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Notes list */}
                {notesLoading ? (
                  <div className="flex items-center gap-2 py-4 justify-center">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                    <span className="text-xs text-slate-400">Loading notes...</span>
                  </div>
                ) : notes.length > 0 ? (
                  <div className="mt-3 space-y-3">
                    {notes.map((note) => {
                      const authorName =
                        note.authorName ||
                        (typeof note.authorId === "object" ? note.authorId?.name : null) ||
                        "Unknown";
                      const initials = getInitials(authorName);
                      return (
                        <div key={note._id} className="flex items-start gap-2.5">
                          <div
                            className="h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: getAvatarColor(authorName) }}
                          >
                            <span className="text-[10px] font-bold text-white leading-none">{initials}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-slate-700">{authorName}</span>
                              <span className="text-[10px] text-slate-400">
                                {new Date(note.createdAt).toLocaleDateString("en-CA", {
                                  month: "short",
                                  day: "numeric",
                                  hour: "numeric",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap leading-relaxed">
                              {note.body}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ConversationClientInspectorDrawer;
